import './BarChart.scss';
import { FC, useEffect, useState } from 'react';
import {
  BarChartEntry as TEntry,
  BarChartPattern,
  BarRanges,
  Sizes,
} from './BarChart.types';
import { SIZES } from './sizes';
import { Layout } from '../../enums';
import { PrevNext } from '../PrevNext/PrevNext';
import { BarChartEntry } from '../BarChartEntry/BarChartEntry';
import { BarChartScaleLines } from '../BarChartScale/BarChartScaleLines';
import { BarChartScaleText } from '../BarChartScale/BarChartScaleText';
import { BarChartLabels } from '../BarChartLabels/BarChartLabels';

interface Props {
  isMobile: boolean;
  entries: TEntry[];
  ranges: BarRanges;
  scalePattern: BarChartPattern;
  valuePattern: BarChartPattern;
  sizes?: Record<Layout, Sizes>;
  isPrimary: boolean;
}

export const BarChart: FC<Props> = ({
  isMobile,
  entries,
  ranges: { primary, secondary },
  scalePattern,
  valuePattern,
  isPrimary,
  sizes = SIZES,
}) => {
  const [offset, setOffset] = useState(0);
  const horizontalRulesLength = primary.max / primary.step;
  const { entryWidth, maxBarHeight, topPadding, width } = isMobile
    ? sizes[Layout.Mobile]
    : sizes[Layout.Desktop];
  const barsPerSlide = isMobile ? 1 : 5;
  const activeRange = isPrimary ? primary : secondary;
  const scaleValFn = isPrimary ? scalePattern.primary : scalePattern.secondary;
  const scaleProps = {
    length: horizontalRulesLength,
    range: activeRange,
    height: maxBarHeight,
    topIndent: topPadding,
  };
  useEffect(() => {
    setOffset(0);
  }, [isMobile, isPrimary]);
  return (
    <div className="bar-chart__mask">
      <BarChartScaleLines {...scaleProps} />
      <div className="bar-chart__gradient-desktop" />
      <BarChartScaleText
        {...scaleProps}
        range={activeRange}
        renderValue={scaleValFn}
        isPrimary={isPrimary}
      />
      <div
        className="bar-chart__wrapper"
        style={{
          transform: `translateX(-${((entryWidth * offset) / width) * 100}vw)`,
        }}
      >
        <BarChartLabels entries={entries} />
        <div className="bar-chart__entries-wrapper">
          {entries.map(({ primaryValue, secondaryValue, label }, i) => {
            const visibleIndex =
              i <= offset + barsPerSlide - 1 ? i % barsPerSlide : -1;
            return (
              <BarChartEntry
                key={typeof label === 'string' ? label : label}
                isPrimary={isPrimary}
                visibleIndex={visibleIndex}
                primaryFormatterFn={valuePattern.primary}
                secondaryFormatterFn={valuePattern.secondary}
                primaryMax={primary.max}
                primaryValue={primaryValue}
                secondaryMax={secondary.max}
                secondaryValue={secondaryValue}
              />
            );
          })}
        </div>
      </div>
      <PrevNext
        entriesLength={entries.length}
        barsPerSlide={barsPerSlide}
        offset={offset}
        onPrev={() => setOffset((offset) => offset - barsPerSlide)}
        onNext={() => setOffset((offset) => offset + barsPerSlide)}
      />
    </div>
  );
};
